import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale, Award, Users, Clock, Download, ChevronRight } from 'lucide-react';
import LiveTicker from '../components/LiveTicker';
import PracticeAreaCard from '../components/PracticeAreaCard';
import InteractiveMap from '../components/InteractiveMap';
import FAQAccordion from '../components/FAQAccordion';
import { practiceAreas, resources, faqs } from '../data';

const stats = [
  { icon: Award, value: '25+', label: 'Years Experience' },
  { icon: Scale, value: '$2B+', label: 'Recovered for Clients' },
  { icon: Users, value: '5,000+', label: 'Cases Won' },
  { icon: Clock, value: '98%', label: 'Success Rate' },
];

export default function HomePage() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#c9a84c]/3 rounded-full blur-[120px]" />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(rgba(201,168,76,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="gold-line" />
              <span className="text-xs font-bold text-[#c9a84c] uppercase tracking-[0.25em]">Summit Legal Group</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#e8e4dc] leading-[1.1] mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Justice Delivered<br />
              <span className="text-[#c9a84c]">With Precision</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
              className="text-lg text-[#e8e4dc]/60 leading-relaxed mb-10 max-w-xl"
            >
              For over 25 years, Summit Legal Group has provided aggressive, effective legal representation to individuals and businesses across New Jersey. Your fight is our mission.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105 text-sm"
              >
                Schedule Free Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/practice-areas"
                className="inline-flex items-center gap-2 px-8 py-4 border border-[#c9a84c]/30 text-[#c9a84c] font-medium rounded-xl hover:bg-[#c9a84c]/10 transition-all duration-300 text-sm"
              >
                Explore Practice Areas
              </Link>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="p-6 rounded-xl bg-[#0f2240]/30 border border-[#c9a84c]/10 text-center hover:border-[#c9a84c]/20 transition-colors">
                <stat.icon className="w-6 h-6 text-[#c9a84c] mx-auto mb-3" />
                <div className="text-3xl font-bold text-[#e8e4dc] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{stat.value}</div>
                <div className="text-xs text-[#e8e4dc]/50 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Live Ticker */}
      <LiveTicker />

      {/* Practice Areas */}
      <section className="py-24 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#c9a84c]/3 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="gold-line mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our <span className="text-[#c9a84c]">Practice Areas</span>
            </h2>
            <p className="text-[#e8e4dc]/60 max-w-lg mx-auto">
              Comprehensive legal services across six specialized practice areas, each led by experienced attorneys dedicated to your success.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {practiceAreas.map((area, idx) => (
              <PracticeAreaCard key={area.id} area={area} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#c9a84c]/10 via-[#c9a84c]/5 to-[#c9a84c]/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a1628]/50 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Facing a <span className="text-[#c9a84c]">Legal Challenge?</span>
            </h2>
            <p className="text-[#e8e4dc]/60 mb-8 max-w-lg mx-auto">
              Don't navigate the legal system alone. Our experienced attorneys are ready to fight for your rights and deliver the results you deserve.
            </p>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105"
            >
              Schedule Your Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Resources Preview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Legal <span className="text-[#c9a84c]">Resources</span>
            </h2>
            <p className="text-[#e8e4dc]/60 max-w-lg mx-auto">
              Free downloadable guides, checklists, and whitepapers to help you understand your legal options.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {resources.slice(0, 3).map((res, idx) => (
              <motion.div
                key={res.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-6 rounded-2xl bg-gradient-to-b from-[#0f2240]/60 to-[#0a1628]/60 border border-[#c9a84c]/10 hover:border-[#c9a84c]/20 transition-all group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center mb-4 group-hover:bg-[#c9a84c]/20 transition-colors">
                  <Download className="w-6 h-6 text-[#c9a84c]" />
                </div>
                <span className="text-xs font-bold text-[#c9a84c] uppercase tracking-wider">{res.category}</span>
                <h3 className="text-lg font-bold text-[#e8e4dc] mt-2 mb-3 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {res.title}
                </h3>
                <p className="text-sm text-[#e8e4dc]/50 leading-relaxed">{res.description}</p>
                <div className="flex items-center gap-3 mt-4 text-xs text-[#e8e4dc]/40">
                  <span>{res.fileSize}</span>
                  <span>•</span>
                  <span>{res.downloads.toLocaleString()} downloads</span>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-[#c9a84c] font-medium hover:text-[#e0cb82] transition-colors">
              View All Resources <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-24 bg-gradient-to-b from-[#0a1628]/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Our <span className="text-[#c9a84c]">Office</span>
            </h2>
            <p className="text-[#e8e4dc]/60 max-w-lg mx-auto">
              Conveniently located in Jersey City, just minutes from the Holland Tunnel and PATH transit.
            </p>
          </motion.div>
          <InteractiveMap />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked <span className="text-[#c9a84c]">Questions</span>
            </h2>
            <p className="text-[#e8e4dc]/60 max-w-lg mx-auto">
              Find answers to common questions about our services, fees, and client portal.
            </p>
          </motion.div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </motion.div>
  );
}
